import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, AlertTriangle, XCircle, Info, Loader2, Inbox,
  WifiOff, ArrowLeft,
} from "lucide-react";
import { Logo, StatusBadge } from "@/components/brand";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const COLORS = [
  { name: "Primary accent", hex: "#EA580C", tw: "bg-orange-600", note: "Buttons, links, highlights (from logo gold)" },
  { name: "Accent hover", hex: "#C2410C", tw: "bg-orange-700", note: "Pressed / hover state" },
  { name: "Accent light", hex: "#FFEDD5", tw: "bg-orange-100", note: "Soft highlight backgrounds" },
  { name: "Logo green", hex: "#16A34A", tw: "bg-emerald-600", note: "Success, connected, approve" },
  { name: "WhatsApp green", hex: "#10B981", tw: "bg-emerald-500", note: "WhatsApp channel accents" },
  { name: "Email blue", hex: "#0EA5E9", tw: "bg-sky-500", note: "Email channel accents" },
  { name: "Order violet", hex: "#8B5CF6", tw: "bg-violet-500", note: "Orders and AI moments" },
  { name: "Social rose", hex: "#F43F5E", tw: "bg-rose-500", note: "Social media accents" },
  { name: "Logo yellow", hex: "#FFD700", tw: "bg-[#FFD700]", note: "Small brand highlights only" },
  { name: "Text primary", hex: "#0F172A", tw: "bg-slate-900", note: "Headings and body text" },
  { name: "Text secondary", hex: "#475569", tw: "bg-slate-600", note: "Descriptions" },
  { name: "Background alt", hex: "#FFF7ED", tw: "bg-orange-50 ring-1 ring-slate-200", note: "Warm alternating sections" },
];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-200";

function Section({ title, desc, children, testid }) {
  return (
    <section className="border-t border-slate-100 py-12" data-testid={testid}>
      <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
      {desc && <p className="mt-1 text-sm text-slate-500">{desc}</p>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

export default function DesignSystem() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sw, setSw] = useState(true);

  return (
    <div className="min-h-screen bg-white" data-testid="design-system-page">
      <header className="border-b border-slate-100">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Logo size="sm" />
          <Link to="/" data-testid="ds-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pb-20 sm:px-6">
        <div className="py-12">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-700">Creative Solution AI</p>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Design System
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-600">
            The shared building blocks used on every page — colors, typography, buttons, cards,
            form fields, badges and alerts. Recreate these one-to-one in WordPress.
          </p>
        </div>

        <Section title="Colors" desc="White background, dark charcoal text, a vibrant orange accent from the logo, plus one bright color per channel. Green only for success and connected states." testid="ds-colors">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {COLORS.map((c) => (
              <div key={c.name} className="overflow-hidden rounded-2xl border border-slate-100" data-testid={`color-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
                <div className={`h-20 ${c.tw}`} />
                <div className="p-3.5">
                  <p className="text-sm font-bold text-slate-900">{c.name}</p>
                  <p className="font-mono text-xs text-slate-500">{c.hex}</p>
                  <p className="mt-1 text-xs text-slate-400">{c.note}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Typography" desc="Outfit for headings, Plus Jakarta Sans for body text." testid="ds-typography">
          <div className="space-y-5 rounded-2xl border border-slate-100 p-6 md:p-8">
            <p className="font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">H1 — Your Complete AI Employee</p>
            <p className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">H2 — Everything your business needs</p>
            <p className="font-display text-xl font-semibold text-slate-900">H3 — Card and subsection titles</p>
            <p className="text-base text-slate-600">Body — Simple, readable sentences that any business owner understands without technical knowledge.</p>
            <p className="text-sm text-slate-500">Small — Supporting details, timestamps and captions.</p>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-700">Eyebrow — Section labels</p>
          </div>
        </Section>

        <Section title="Buttons" desc="Large, touch-friendly (minimum 44px height), one clear action per view." testid="ds-buttons">
          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="ds-btn-primary" className="rounded-xl bg-orange-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-700">Primary</button>
            <button data-testid="ds-btn-secondary" className="rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800">Secondary</button>
            <button data-testid="ds-btn-outline" className="rounded-xl border-2 border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50">Outline</button>
            <button data-testid="ds-btn-ghost" className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100">Ghost</button>
            <button data-testid="ds-btn-success" className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">Approve</button>
            <button data-testid="ds-btn-danger" className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700">Reject</button>
            <button data-testid="ds-btn-loading" disabled className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-6 py-3 text-sm font-semibold text-white opacity-70">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading
            </button>
          </div>
        </Section>

        <Section title="Badges and statuses" desc="One consistent style for every connection and conversation state." testid="ds-badges">
          <div className="flex flex-wrap gap-2.5">
            {["Working", "Connected", "Active", "Needs attention", "Paused", "Disconnected", "Setup required", "Not connected", "Needs you", "Resolved"].map((s) => (
              <StatusBadge key={s} label={s} />
            ))}
          </div>
        </Section>

        <Section title="Cards" desc="Rounded 16px, thin border, soft shadow on hover." testid="ds-cards">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-100 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid="ds-card-feature">
              <div className="inline-flex rounded-xl bg-orange-50 p-2.5 text-orange-600"><Info className="h-5 w-5" /></div>
              <p className="mt-4 font-display text-base font-bold text-slate-900">Feature card</p>
              <p className="mt-1 text-sm text-slate-500">Used on the homepage and dashboards.</p>
            </div>
            <div className="rounded-2xl border-2 border-orange-500 bg-white p-6 shadow-lg" data-testid="ds-card-highlight">
              <span className="rounded-full bg-orange-600 px-3 py-1 text-xs font-bold text-white">Most Popular</span>
              <p className="mt-4 font-display text-base font-bold text-slate-900">Highlighted card</p>
              <p className="mt-1 text-sm text-slate-500">For the recommended plan or action.</p>
            </div>
            <div className="rounded-2xl border border-orange-200 bg-orange-50/50 p-6" data-testid="ds-card-attention">
              <div className="inline-flex rounded-xl bg-orange-100 p-2.5 text-orange-700"><AlertTriangle className="h-5 w-5" /></div>
              <p className="mt-4 font-display text-base font-bold text-slate-900">Attention card</p>
              <p className="mt-1 text-sm text-slate-500">Approvals and things needing you.</p>
            </div>
          </div>
        </Section>

        <Section title="Form fields" desc="Rounded 12px, clear focus ring in orange, helpful placeholder text." testid="ds-form-fields">
          <div className="grid max-w-2xl gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Default</label>
              <input data-testid="ds-input-default" placeholder="Business name" className={inputCls} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Disabled</label>
              <input data-testid="ds-input-disabled" disabled placeholder="Not editable" className={`${inputCls} cursor-not-allowed bg-slate-50 text-slate-400`} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Error</label>
              <input data-testid="ds-input-error" defaultValue="not-an-email" className="w-full rounded-xl border border-red-300 bg-red-50/40 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-red-200" />
              <p className="mt-1.5 text-xs font-medium text-red-600">Please enter a valid email address.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Success</label>
              <input data-testid="ds-input-success" defaultValue="sharmaelectronics.in" className="w-full rounded-xl border border-emerald-300 bg-emerald-50/40 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-200" />
              <p className="mt-1.5 text-xs font-medium text-emerald-600">Website connected successfully.</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="ds-switch" checked={sw} onCheckedChange={setSw} />
              <span className="text-sm font-medium text-slate-700">Switch — {sw ? "on" : "off"}</span>
            </div>
          </div>
        </Section>

        <Section title="Alerts and states" desc="Success, warning, error, loading, empty and connection-failed states." testid="ds-alerts">
          <div className="grid max-w-3xl gap-3">
            {[
              { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-800", text: "Success — Your changes have been saved.", testid: "ds-alert-success" },
              { icon: AlertTriangle, cls: "border-orange-200 bg-orange-50 text-orange-800", text: "Warning — Your Gmail connection needs attention.", testid: "ds-alert-warning" },
              { icon: XCircle, cls: "border-red-200 bg-red-50 text-red-700", text: "Error — The file could not be uploaded. Please try again.", testid: "ds-alert-error" },
              { icon: WifiOff, cls: "border-red-200 bg-red-50 text-red-700", text: "Connection failed — We could not reach WhatsApp. Check your internet and reconnect.", testid: "ds-alert-connection-failed" },
            ].map((a) => (
              <div key={a.testid} data-testid={a.testid} className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 text-sm font-medium ${a.cls}`}>
                <a.icon className="h-5 w-5 shrink-0" /> {a.text}
              </div>
            ))}
            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-600" data-testid="ds-loading-state">
              <Loader2 className="h-5 w-5 animate-spin text-orange-600" /> Loading — Reading your website, please wait...
            </div>
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center" data-testid="ds-empty-state">
              <Inbox className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-2 font-display text-sm font-bold text-slate-700">Nothing here yet</p>
              <p className="text-xs text-slate-400">New conversations will appear here once your AI employee starts working.</p>
            </div>
          </div>
        </Section>

        <Section title="Confirmation dialog" desc="Used before every important action — approvals, go-live, takeovers." testid="ds-dialog">
          <button
            data-testid="ds-open-dialog-button"
            onClick={() => setDialogOpen(true)}
            className="rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Open example dialog
          </button>
          <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <AlertDialogContent data-testid="ds-example-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display">Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  Important actions always ask first. Explain what happens in one plain sentence.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="ds-dialog-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="ds-dialog-confirm" onClick={() => toast.success("Confirmed")}>Confirm</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Section>
      </main>
    </div>
  );
}
